/**
 * 「刚恢复」那个窗口。
 *
 * 动态中心要把**重连之后第一批补回来的条目**单独数一个数给用户看
 * （「补回来 N 条」），之后的实时条目就不再算了。判据是时间：
 * 连上之后的头 50ms 里到的算，再往后不算。
 *
 * # 为什么它值得单独一个模块
 *
 * 它原来是屏里三行裸代码：一个 `wasDown` ref 加一个裸的
 * `window.setTimeout`。三行里藏了两个真会走到的缺陷，而两个都**不报错**，
 * 只让那个数字或那行提示悄悄不对：
 *
 * 1. **卸载之后那个定时器照样跑一次。**
 * 2. **连上之后 50ms 之内又断了** —— 旧定时器到点把「刚断过」清成假，
 *    于是**后面真正那次恢复不再解除「连接断开」**，屏幕上那行字
 *    再也不会消失，而流其实早就接回来了。1 秒退避加上服务端半死不活
 *    （连上就塌）时，OPEN 与断开相隔不到 50ms 一点都不罕见。
 *
 * 抽出来是为了它**守得住**：屏里那三行要验一次得把整屏渲染起来
 * （路由 + query client + 假后端），那种用例又慢又脆，
 * 于是现实里就是没人写——而没人写的地方正是这两个缺陷待的地方。
 */
export interface RecoveredWindow {
  /**
   * 断了。窗口当场作废，并记下「刚断过」。
   *
   * **作废是这里最要紧的一句**：不作废的话，上一次 OPEN 排下的那个
   * 定时器会在几十毫秒后把「刚断过」清掉，而那时我们明明还断着。
   */
  readonly disconnected: () => void
  /**
   * 连上了。回「这一次是不是从断开中恢复的」。
   *
   * 回真时同时开一个新窗口：从现在起 `counting()` 为真，`ms` 之后转假。
   * 回假说明这是首次连接，没什么可恢复的。
   */
  readonly opened: () => boolean
  /** 此刻到的条目算不算「恢复补回来的」。 */
  readonly counting: () => boolean
  /** 卸载时调。清掉定时器，并让还没到点的那个失效。 */
  readonly dispose: () => void
}

/** 窗口有多长。50ms 够覆盖同一轮送达的那批 backlog。 */
export const RECOVERED_WINDOW_MS = 50

export function createRecoveredWindow(ms: number = RECOVERED_WINDOW_MS): RecoveredWindow {
  let down = false
  let timer: ReturnType<typeof setTimeout> | undefined
  /**
   * 轮次。一轮退避里可能连着接上好几次，每次都排一个定时器；
   * 没有轮次的话先到期的那个会把窗口关掉，而最后那次恢复的 backlog
   * 就不再被算进去——一个悄悄少掉的数字，没有任何东西会红。
   */
  let generation = 0

  const cancel = () => {
    if (timer !== undefined) clearTimeout(timer)
    timer = undefined
    generation += 1
  }

  return {
    disconnected: () => {
      cancel()
      down = true
    },
    opened: () => {
      if (!down) return false
      cancel()
      const mine = generation
      timer = setTimeout(() => {
        if (generation !== mine) return
        timer = undefined
        down = false
      }, ms)
      return true
    },
    counting: () => down,
    dispose: cancel,
  }
}
